import React, { useState } from "react";
import axios from "axios";
import { Clock, Envelope, GeoAlt, Telephone } from "react-bootstrap-icons";
import PageHero from "../../components/public/PageHero";
import Seo from "../../components/public/Seo";
import Notification, { showToast } from "../../components/common/notification/Notification";
import { CONTACT } from "../../components/landing/data/landingData";

const CONTACT_INFO = [
  { icon: GeoAlt, label: "Address", value: CONTACT.address },
  { icon: Telephone, label: "Phone", value: CONTACT.phone, href: CONTACT.phoneHref },
  { icon: Envelope, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
];

// Same request body the form always sent to POST /all/addContactUs
const EMPTY_FORM = { name: "", email_address: "", mobile_number: "", message: "" };

const validate = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Please enter your name";
  if (!form.email_address.trim()) errors.email_address = "Please enter your email";
  else if (!/^\S+@\S+\.\S+$/.test(form.email_address.trim())) errors.email_address = "Enter a valid email address";
  if (!String(form.mobile_number).trim()) errors.mobile_number = "Please enter your phone number";
  else if (String(form.mobile_number).replace(/\D/g, "").length < 10) errors.mobile_number = "Enter a valid 10-digit phone number";
  if (!form.message.trim()) errors.message = "Please tell us how we can help";
  return errors;
};

function ContactUsFront() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    setSent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate(formData);
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`contact-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${baseUrl}/all/addContactUs`, formData);
      showToast("Query sent successfully", { type: "success" });
      setFormData(EMPTY_FORM);
      setSent(true);
    } catch (error) {
      // The form keeps what was typed
      showToast("Something went wrong, please try again", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name, label, props = {}) => (
    <div>
      <label htmlFor={`contact-${name}`} className="lp-search__label">{label}</label>
      <input
        id={`contact-${name}`}
        name={name}
        className="lp-input"
        value={formData[name]}
        onChange={handleChange}
        aria-invalid={errors[name] ? "true" : undefined}
        aria-describedby={errors[name] ? `contact-${name}-error` : undefined}
        {...props}
      />
      {errors[name] && <div className="lp-field-error" id={`contact-${name}-error`} role="alert">{errors[name]}</div>}
    </div>
  );

  return (
    <>
      <Seo
        title="Contact Us | All Home Desire"
        description="Talk to the All Home Desire team about buying, selling or renting a property."
        path="/contactus"
      />
      <Notification />
      <PageHero title="Contact Us" subtitle="Have a question about a property? Drop us a message and we'll get back to you shortly." crumbs={[{ label: "Contact Us" }]} />

      <section className="lp-section">
        <div className="container">
          <div className="row g-5">
            <div className="col-12 col-lg-5">
              <span className="lp-eyebrow">Reach us</span>
              <h2 className="lp-heading__title mb-3">We&apos;d love to hear from you</h2>
              <p className="lp-muted mb-4">Reach out by phone, email or the form and a property expert will respond within 24 hours.</p>
              <ul className="list-unstyled d-grid gap-3 mb-4">
                {CONTACT_INFO.map(({ icon: Icon, label, value, href }) => (
                  <li key={label} className="d-flex gap-3 align-items-start">
                    <span className="lp-icon-box lp-icon-box--sm" aria-hidden="true"><Icon /></span>
                    <span>
                      <strong className="d-block">{label}</strong>
                      {href ? <a href={href} className="lp-link" style={{ overflowWrap: "anywhere" }}>{value}</a> : <span className="lp-muted">{value}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="lp-card p-4 d-flex gap-3 align-items-start">
                <span className="lp-icon-box lp-icon-box--sm" aria-hidden="true"><Clock /></span>
                <span><strong className="d-block">Office location</strong><span className="lp-muted">{CONTACT.address}</span></span>
              </div>
            </div>

            <div className="col-12 col-lg-7">
              <div className="lp-card p-4 p-md-5">
                <h2 className="lp-card__title">Send a Message</h2>
                <p className="lp-muted mb-4">Fill in the details below and we&apos;ll respond within 24 hours.</p>
                <form onSubmit={handleSubmit} noValidate className="d-grid gap-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">{field("name", "Name", { type: "text", placeholder: "Your full name", autoComplete: "name" })}</div>
                    <div className="col-12 col-md-6">{field("email_address", "Email", { type: "email", placeholder: "your@email.com", autoComplete: "email" })}</div>
                  </div>
                  {field("mobile_number", "Phone", { type: "tel", inputMode: "numeric", placeholder: "10-digit phone number", autoComplete: "tel" })}
                  <div>
                    <label htmlFor="contact-message" className="lp-search__label">Message</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      className="lp-input"
                      rows={5}
                      placeholder="Tell us how we can help..."
                      value={formData.message}
                      onChange={handleChange}
                      aria-invalid={errors.message ? "true" : undefined}
                      aria-describedby={errors.message ? "contact-message-error" : undefined}
                    />
                    {errors.message && <div className="lp-field-error" id="contact-message-error" role="alert">{errors.message}</div>}
                  </div>
                  <div aria-live="polite">
                    {sent && <div className="alert alert-success mb-0">Thank you! Your message has been sent. We&apos;ll be in touch shortly.</div>}
                  </div>
                  <button type="submit" className="lp-btn lp-btn--primary lp-btn--lg w-100" disabled={submitting}>
                    {submitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" /> Sending...</>) : "Send Message"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default ContactUsFront;
